import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil } from "lucide-react";
import ConfirmDelete from "@/components/ConfirmDelete";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { useAuth } from "@/context/AuthContext";

const REASONS = ["Training Issues", "Personal", "Behaviour", "Compensation", "Career Growth", "Relocation", "Health", "Other"];
const PALETTE = ["#DC2626", "#C9A24B", "#0F172A", "#059669", "#64748B", "#10B981", "#E5C989", "#1E293B"];

export default function Attrition() {
  const { branch, user } = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    const [a, m, e] = await Promise.all([
      api.get("/attrition").catch(() => ({ data: [] })),
      api.get("/meta").catch(() => ({ data: {} })),
      api.get("/employees").catch(() => ({ data: [] })),
    ]);
    setItems(a.data);
    setMeta(m.data);
    setEmployees(e.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((x) => branch === "All" || x.branch === branch);

  const byReason = useMemo(() => {
    const g = {};
    filtered.forEach((x) => { g[x.reason || "Other"] = (g[x.reason || "Other"] || 0) + 1; });
    return Object.entries(g).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  const byDept = useMemo(() => {
    const g = {};
    filtered.forEach((x) => { g[x.department || "Unknown"] = (g[x.department || "Unknown"] || 0) + 1; });
    return Object.entries(g).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  const earlyPct = filtered.length
    ? Math.round(filtered.filter((x) => x.early_exit).length / filtered.length * 100)
    : 0;

  const save = async () => {
    try {
      const { id, ...body } = form;
      if (id) {
        await api.patch(`/attrition/${id}`, body);
        toast.success("Exit record updated");
      } else {
        await api.post("/attrition", body);
        toast.success("Recorded");
      }
      setOpen(false);
      setForm({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const canManage = ["super_admin", "hr_manager"].includes(user?.role);

  const openEdit = (x) => {
    setForm({
      id: x.id, employee_id: x.employee_id, employee_name: x.employee_name,
      department: x.department, branch: x.branch,
      join_date: x.join_date || "", exit_date: x.exit_date || "",
      reason: x.reason || "", exit_interview_done: !!x.exit_interview_done,
      remarks: x.remarks || "",
    });
    setOpen(true);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/attrition/${id}`);
      toast.success("Exit record deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="attrition-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Attrition Analytics</div>
          <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">Exits &amp; Retention Loss</h1>
        </div>
        {canManage && (
          <Button onClick={() => { setForm({}); setOpen(true); }} className="rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="record-exit-button">
            <Plus className="h-4 w-4 mr-1" /> Record exit
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Stat label="Total exits" value={filtered.length} />
        <Stat label="Early exits (<30d)" value={`${filtered.filter((x) => x.early_exit).length} (${earlyPct}%)`} tone="rose" />
        <Stat label="Exit interviews done" value={`${filtered.filter((x) => x.exit_interview_done).length} / ${filtered.length}`} tone="emerald" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="label-eyebrow mb-3">Reason breakdown</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={byReason} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50} paddingAngle={2}>
                {byReason.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <RTooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
            {byReason.map((it, i) => (
              <div key={it.name} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />
                {it.name} <span className="text-slate-400">({it.value})</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="label-eyebrow mb-3">Department breakdown</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byDept}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
              <RTooltip />
              <Bar dataKey="value" fill="#DC2626" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                <th className="data-cell">Employee</th>
                <th className="data-cell">Dept · Branch</th>
                <th className="data-cell">Joined</th>
                <th className="data-cell">Exited</th>
                <th className="data-cell">Tenure</th>
                <th className="data-cell">Reason</th>
                <th className="data-cell">Exit interview</th>
                <th className="data-cell">Flag</th>
                <th className="data-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="data-cell text-center text-slate-500 py-8">No exits recorded.</td></tr>
              )}
              {filtered.map((x) => (
                <tr key={x.id} className="border-b border-slate-100 data-row" data-testid={`attrition-row-${x.id}`}>
                  <td className="data-cell font-medium text-[var(--yc-navy)]">{x.employee_name}</td>
                  <td className="data-cell text-slate-600">{x.department} · {x.branch}</td>
                  <td className="data-cell text-slate-500">{x.join_date?.slice(0,10)}</td>
                  <td className="data-cell text-slate-500">{x.exit_date?.slice(0,10)}</td>
                  <td className="data-cell font-mono text-xs">{x.tenure_days}d</td>
                  <td className="data-cell text-slate-600">{x.reason}</td>
                  <td className="data-cell text-slate-600">{x.exit_interview_done ? "Yes" : "No"}</td>
                  <td className="data-cell">{x.early_exit ? <span className="rag-red px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold">Early exit</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                  <td className="data-cell">
                    <div className="flex items-center gap-1">
                      {canManage && (
                        <>
                          <button type="button" onClick={() => openEdit(x)} className="p-1.5 rounded-md text-slate-400 hover:text-[var(--yc-navy)] hover:bg-slate-100 transition-colors" title="Edit" data-testid={`edit-attrition-${x.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <ConfirmDelete onConfirm={() => remove(x.id)} label={`exit record for ${x.employee_name}`} testId={`delete-attrition-${x.id}`} />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{form.id ? "Edit exit record" : "Record employee exit"}</DialogTitle>
            <DialogDescription>Log exit details for attrition analytics.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="label-eyebrow">Employee (optional link)</Label>
              <Select value={form.employee_id || ""} onValueChange={(v) => {
                const emp = employees.find(e => e.id === v);
                setForm({ ...form,
                  employee_id: v,
                  employee_name: emp?.name || form.employee_name,
                  department: emp?.department || form.department,
                  branch: emp?.branch || form.branch,
                  join_date: emp?.joining_date || form.join_date,
                });
              }}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pick active employee" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {employees.filter((e) => e.employment_status === "Active").map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name} — {e.department}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="label-eyebrow">Name</Label><Input value={form.employee_name || ""} onChange={(e) => setForm({...form, employee_name: e.target.value})} className="mt-1.5" /></div>
            <div>
              <Label className="label-eyebrow">Department</Label>
              <Select value={form.department || ""} onValueChange={(v) => setForm({...form, department: v})}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.departments?.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Branch</Label>
              <Select value={form.branch || ""} onValueChange={(v) => setForm({...form, branch: v})}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.branches?.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-eyebrow">Join date</Label><Input type="date" value={(form.join_date || "").slice(0,10)} onChange={(e) => setForm({...form, join_date: e.target.value})} className="mt-1.5" /></div>
            <div><Label className="label-eyebrow">Exit date</Label><Input type="date" value={(form.exit_date || "").slice(0,10)} onChange={(e) => setForm({...form, exit_date: e.target.value})} className="mt-1.5" /></div>
            <div>
              <Label className="label-eyebrow">Reason</Label>
              <Select value={form.reason || ""} onValueChange={(v) => setForm({...form, reason: v})}>
                <SelectTrigger className="mt-1.5" data-testid="exit-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Exit interview</Label>
              <Select value={form.exit_interview_done ? "yes" : "no"} onValueChange={(v) => setForm({...form, exit_interview_done: v === "yes"})}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="yes">Done</SelectItem><SelectItem value="no">Not done</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="label-eyebrow">Remarks</Label><Textarea rows={2} value={form.remarks || ""} onChange={(e) => setForm({...form, remarks: e.target.value})} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="save-attrition-button">{form.id ? "Save changes" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone = "navy" }) {
  const cls = tone === "rose" ? "text-[var(--yc-rose)]" : tone === "emerald" ? "text-[var(--yc-emerald)]" : "text-[var(--yc-navy)]";
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="label-eyebrow">{label}</div>
      <div className={`font-heading font-bold text-3xl mt-2 ${cls}`}>{value}</div>
    </div>
  );
}
